/// <reference lib="WebWorker" />
/**
 * Diff worker entry. Built as a separate rollup output (`dist/worker.js`).
 *
 * The worker is intentionally narrow: it consumes a `ComputeRequest`,
 * produces a `LinesDiff`, converts it to the wire shape, and posts it back.
 * All `LinesDiff -> DiffResult` adapter work happens on the main thread,
 * which already has the input strings.
 */

import { DefaultLinesDiffComputer, ILinesDiffComputerOptions } from '../defaultLinesDiffComputer/defaultLinesDiffComputer.js';
import { LinesDiff, DetailedLineRangeMapping, LineRange, RangeMapping } from '../defaultLinesDiffComputer/rangeMapping.js';
import { Range } from '../defaultLinesDiffComputer/linesSliceCharSequence.js';
import { initWasm, getWasm } from '../wasm/loader.js';
import type { WasmLinesDiff, WasmDiffOptions } from '../wasm/types.js';
import type { DiffOptions } from '../api/diffResult.js';
import { linesDiffToWire } from './wire.js';
import type {
    ComputeRequest,
    ComputeResponse,
    InitRequest,
    InitResponse,
    WorkerRequest,
} from './protocol.js';

const tsImpl = new DefaultLinesDiffComputer();

self.addEventListener('message', (ev: MessageEvent<WorkerRequest>) => {
    const msg = ev.data;
    if (msg.kind === 'init') {
        void handleInit(msg);
    } else if (msg.kind === 'computeDiff') {
        void handleCompute(msg);
    }
});

async function handleInit(req: InitRequest): Promise<void> {
    try {
        if (req.backend === 'wasm') {
            await initWasm();
        }
        const resp: InitResponse = { kind: 'init', id: req.id, ok: true };
        self.postMessage(resp);
    } catch (err) {
        const resp: ComputeResponse = {
            kind: 'computeDiff',
            id: req.id,
            ok: false,
            error: String(err instanceof Error ? err.message : err),
        };
        self.postMessage(resp);
    }
}

async function handleCompute(req: ComputeRequest): Promise<void> {
    try {
        let linesDiff: LinesDiff;
        if (req.backend === 'ts') {
            linesDiff = computeTs(req.originalLines, req.modifiedLines, req.options);
        } else {
            await initWasm();
            linesDiff = computeWasm(req.originalLines, req.modifiedLines, req.options);
        }
        const result = linesDiffToWire(linesDiff);
        const resp: ComputeResponse = { kind: 'computeDiff', id: req.id, ok: true, result };
        self.postMessage(resp);
    } catch (err) {
        const resp: ComputeResponse = {
            kind: 'computeDiff',
            id: req.id,
            ok: false,
            error: String(err instanceof Error ? err.message : err),
        };
        self.postMessage(resp);
    }
}

function computeTs(original: string[], modified: string[], options: DiffOptions): LinesDiff {
    const internal: ILinesDiffComputerOptions = toInternalOptions(options);
    return tsImpl.computeDiff(original, modified, internal);
}

function computeWasm(
    original: string[],
    modified: string[],
    options: DiffOptions,
): LinesDiff {
    const wasm = getWasm();
    const wasmOptions: WasmDiffOptions = toWasmOptions(options);
    const wasmResult: WasmLinesDiff = wasm.computeDiffLines(original, modified, wasmOptions);
    return wasmLinesDiffToLinesDiff(wasmResult);
}

function wasmLinesDiffToLinesDiff(wasmResult: WasmLinesDiff): LinesDiff {
    const changes = wasmResult.changes.map((c) => {
        const inner = c.innerChanges?.map((ic) =>
            new RangeMapping(
                new Range(ic.originalStartLine, ic.originalStartColumn, ic.originalEndLine, ic.originalEndColumn),
                new Range(ic.modifiedStartLine, ic.modifiedStartColumn, ic.modifiedEndLine, ic.modifiedEndColumn),
            ),
        );
        return new DetailedLineRangeMapping(
            new LineRange(c.original.startLineNumber, c.original.endLineNumberExclusive),
            new LineRange(c.modified.startLineNumber, c.modified.endLineNumberExclusive),
            inner,
        );
    });
    return new LinesDiff(changes, [], wasmResult.hitTimeout);
}

function toInternalOptions(options: DiffOptions): ILinesDiffComputerOptions {
    return {
        maxComputationTimeMs: options?.maxComputationTimeMs ?? 0,
        ignoreTrimWhitespace: options?.ignoreTrimWhitespace ?? false,
        computeMoves: options?.computeMoves ?? false,
        extendToSubwords: options?.extendToSubwords ?? false,
    };
}

function toWasmOptions(options: DiffOptions): WasmDiffOptions {
    const out: WasmDiffOptions = {};
    if (options?.maxComputationTimeMs !== undefined) out.maxComputationTimeMs = options.maxComputationTimeMs;
    if (options?.ignoreTrimWhitespace !== undefined) out.ignoreTrimWhitespace = options.ignoreTrimWhitespace;
    if (options?.computeMoves !== undefined) out.computeMoves = options.computeMoves;
    if (options?.extendToSubwords !== undefined) out.extendToSubwords = options.extendToSubwords;
    return out;
}
